import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Paperclip, Pencil, Upload, FileUp, Send, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageWrapper from '@/components/layout/PageWrapper';
import StatusBadge from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import POForm from '@/components/orderedParts/POForm';
import { repairRequestsApi, orderedPartsApi, settingsApi } from '@/api';
import { useAuth } from '@/auth/AuthContext';
import { formatDate, cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import type { RepairRequest, OrderedPart, PartAttachment, RepairRequestNote } from '@/types';

const editSchema = z.object({
  status: z.enum(['New', 'In-Progress', 'Parts Ordered', 'Done']),
  mechanicNotes: z.string().optional(),
  mechanicRepairDate: z.string().optional(),
  mechanicRepairHours: z.coerce.number().min(0).max(999).optional(),
});

type EditFormData = z.infer<typeof editSchema>;

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export default function RepairRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole, currentUser } = useAuth();
  const [addPartOpen, setAddPartOpen] = useState(false);
  const [editPart, setEditPart] = useState<OrderedPart | null>(null);
  const [receiptPart, setReceiptPart] = useState<OrderedPart | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const receiptInputRef = useRef<HTMLInputElement>(null);

  const canEdit = hasRole(['repair_tech', 'pm_admin', 'super_admin', 'equipment_admin']);

  // Edit dialog (submitter editing their own request)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editProblem, setEditProblem] = useState('');
  const [editDriverName, setEditDriverName] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Notes
  const [noteInput, setNoteInput] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  const { data: request, isLoading } = useQuery({
    queryKey: ['repairRequest', id],
    queryFn: () => repairRequestsApi.get(id!),
    enabled: !!id,
  });

  const { data: partsData, refetch: refetchParts } = useQuery({
    queryKey: ['orderedParts', 'request', id],
    queryFn: () => orderedPartsApi.list({ request_id: id }),
    enabled: !!id,
  });

  const parts: OrderedPart[] = partsData?.items ?? [];

  const { data: notes = [], refetch: refetchNotes } = useQuery<RepairRequestNote[]>({
    queryKey: ['repairRequestNotes', id],
    queryFn: () => repairRequestsApi.getNotes(id!),
    enabled: !!id && !hasRole(['ext_vendor']),
  });

  const { data: receiptAttachments = [], refetch: refetchAttachments } = useQuery<PartAttachment[]>({
    queryKey: ['orderedParts', 'attachments', receiptPart?.id],
    queryFn: () => orderedPartsApi.listAttachments(receiptPart!.id),
    enabled: !!receiptPart,
  });

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });

  const updateMutation = useMutation({
    mutationFn: (data: EditFormData) =>
      repairRequestsApi.update(id!, {
        ...data,
        mechanicRepairDate: data.mechanicRepairDate || undefined,
        mechanicId: currentUser?.id,
      }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['repairRequest', id] });
      queryClient.invalidateQueries({ queryKey: ['repairRequests'] });
      if (updated.status === 'Done') {
        navigate('/repair-requests');
      } else {
        toast({ title: 'Repair request updated' });
      }
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({ title: message ?? 'Failed to update', variant: 'destructive' });
    },
  });

  const form = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    values: {
      status: (request as RepairRequest | undefined)?.status ?? 'New',
      mechanicNotes: (request as RepairRequest | undefined)?.mechanicNotes ?? '',
      mechanicRepairDate: (request as RepairRequest | undefined)?.mechanicRepairDate ?? '',
      mechanicRepairHours: (request as RepairRequest | undefined)?.mechanicRepairHours ?? undefined,
    },
  });

  const isOwnRequest = !!request && request.submittedBy === currentUser?.id;


  if (isLoading) {
    return (
      <PageWrapper title="Loading…">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </PageWrapper>
    );
  }

  if (!request) {
    return (
      <PageWrapper title="Not Found">
        <p className="text-muted-foreground">Repair request not found.</p>
      </PageWrapper>
    );
  }

  async function handleReceiptUpload() {
    if (!receiptPart || !receiptFile) return;
    setUploading(true);
    try {
      const contentType = receiptFile.type || 'application/octet-stream';
      const { uploadUrl, s3Key } = await orderedPartsApi.presignAttachment(
        receiptPart.id,
        receiptFile.name,
        contentType,
      );
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: receiptFile,
      });
      await orderedPartsApi.recordAttachment(receiptPart.id, s3Key, receiptFile.name);
      if (receiptPart.poStatus !== 'Done') {
        await orderedPartsApi.update(String(receiptPart.id), { poStatus: 'Done' });
      }
      await Promise.all([refetchParts(), refetchAttachments()]);
      toast({ title: 'Receipt uploaded' });
      setReceiptFile(null);
    } catch {
      toast({ title: 'Failed to upload receipt', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  }

  function openEditDialog() {
    setEditProblem(request?.problem ?? '');
    setEditDriverName(request?.driverName ?? '');
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!editProblem.trim()) return;
    setIsSavingEdit(true);
    try {
      await repairRequestsApi.update(id!, { problem: editProblem.trim(), driverName: editDriverName.trim() || undefined });
      queryClient.invalidateQueries({ queryKey: ['repairRequest', id] });
      queryClient.invalidateQueries({ queryKey: ['repairRequests'] });
      toast({ title: 'Request updated' });
      setEditOpen(false);
    } catch {
      toast({ title: 'Failed to save', variant: 'destructive' });
    } finally {
      setIsSavingEdit(false);
    }
  }

  async function addNote() {
    if (!noteInput.trim()) return;
    setIsSavingNote(true);
    try {
      await repairRequestsApi.addNote(id!, noteInput.trim());
      setNoteInput('');
      await refetchNotes();
    } catch {
      toast({ title: 'Failed to add note', variant: 'destructive' });
    } finally {
      setIsSavingNote(false);
    }
  }

  async function onSubmit(values: EditFormData) {
    if (values.status === 'Done') {
      const unreceived = parts.filter((p) => p.poStatus !== 'Done' && !p.returned);
      if (unreceived.length > 0) {
        toast({
          title: 'Cannot mark as Done',
          description: `${unreceived.length} part(s) have not been received yet.`,
          variant: 'destructive',
        });
        return;
      }
      const missingReceipt = parts.filter(
        (p) => p.poStatus === 'Done' && Number(p.attachmentCount ?? 0) === 0
      );
      if (missingReceipt.length > 0) {
        toast({
          title: 'Cannot mark as Done',
          description: `"${missingReceipt[0].title}"${missingReceipt.length > 1 ? ` and ${missingReceipt.length - 1} other(s)` : ''} missing a receipt — upload before closing.`,
          variant: 'destructive',
        });
        return;
      }
      const notesRequired = settings?.mechanicNotesRequired !== 'false';
      if (notesRequired && !values.mechanicNotes?.trim()) {
        form.setError('mechanicNotes', { message: 'Mechanic notes are required before marking Done' });
        toast({
          title: 'Mechanic notes required',
          description: 'Please enter mechanic notes before closing this request.',
          variant: 'destructive',
        });
        return;
      }
    }
    await updateMutation.mutateAsync(values);
  }

  return (
    <PageWrapper
      title={`Request #${String(request.id).padStart(6, '0')}`}
      actions={
        <div className="flex gap-2">
          {(isOwnRequest || canEdit) && request?.status !== 'Done' && (
            <Button variant="outline" size="sm" onClick={openEditDialog}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/repair-requests')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      }
    >
      <div className="space-y-6">

          {/* Request Details (read-only) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Equipment</p>
                  <p className="font-medium">
                    {request.equipment?.unitNumber ?? '—'}
                    {request.equipment?.description ? ` — ${request.equipment.description}` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Company</p>
                  <p className="font-medium">{request.equipment?.company ?? '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Repair Order Date</p>
                  <p className="font-medium">{formatDate(request.requestDate)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <StatusBadge status={request.status} />
                </div>
                {request.driverName && (
                  <div>
                    <p className="text-muted-foreground">Driver Name</p>
                    <p className="font-medium">{request.driverName}</p>
                  </div>
                )}
                {request.truckOrTrailer && (
                  <div>
                    <p className="text-muted-foreground">Equipment Type</p>
                    <p className="font-medium">{request.truckOrTrailer}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="mb-1 text-sm text-muted-foreground">Problem or Issue</p>
                <p className="text-sm leading-relaxed">{request.problem}</p>
              </div>

              {request.autoGenerated && (
                <span className={cn(
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                )}>
                  Auto-generated PM
                </span>
              )}
            </CardContent>
          </Card>

          {/* Notes — chat thread, visible to all non-ext_vendor */}
          {!hasRole(['ext_vendor']) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Thread */}
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {notes.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No notes yet.</p>
                  )}
                  {notes.map((n) => {
                    const isMe = n.userId === currentUser?.id;
                    return (
                      <div key={n.id} className={cn('flex flex-col gap-0.5', isMe ? 'items-end' : 'items-start')}>
                        <div className={cn(
                          'max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap',
                          isMe
                            ? 'bg-primary text-primary-foreground rounded-br-sm'
                            : 'bg-muted text-foreground rounded-bl-sm'
                        )}>
                          {n.note}
                        </div>
                        <p className="text-[11px] text-muted-foreground px-1">
                          {isMe ? 'You' : n.userName} · {new Date(n.createdAt).toLocaleString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {/* Input */}
                <div className="flex gap-2 pt-1">
                  <Textarea
                    placeholder="Add a note…"
                    rows={2}
                    className="resize-none"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        addNote();
                      }
                    }}
                  />
                  <Button
                    size="icon"
                    className="shrink-0 self-end"
                    disabled={isSavingNote || !noteInput.trim()}
                    onClick={addNote}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ordered Parts inline table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Ordered Parts or Third Party</CardTitle>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => setAddPartOpen(true)}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  New
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-muted-foreground text-xs uppercase tracking-wide">
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-left">Date of Repair</th>
                      <th className="px-4 py-2 text-left">PO#</th>
                      <th className="px-4 py-2 text-left">Cost</th>
                      <th className="px-4 py-2 text-left">Receipts</th>
                      <th className="px-4 py-2 text-left">Returned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground text-xs">
                          No parts ordered yet.
                        </td>
                      </tr>
                    ) : (
                      parts.map((part) => {
                        const hasAttachment = Number(part.attachmentCount ?? 0) > 0;
                        return (
                          <tr
                            key={part.id}
                            className={cn(
                              'border-b border-border cursor-pointer transition-opacity hover:opacity-80',
                              hasAttachment
                                ? 'bg-green-50 dark:bg-green-900/20'
                                : 'bg-amber-50 dark:bg-amber-900/20'
                            )}
                            onClick={() => setEditPart(part)}
                            title="Click to edit"
                          >
                            <td className="px-4 py-2 font-medium">{part.title}</td>
                            <td className="px-4 py-2">{part.repairDate ? formatDate(part.repairDate) : '—'}</td>
                            <td className="px-4 py-2 font-mono">{part.poNumber}</td>
                            <td className="px-4 py-2">{formatCents(part.quotedPrice)}{part.actualPrice != null && <span className="text-muted-foreground text-xs ml-1">/ {formatCents(part.actualPrice)}</span>}</td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {hasAttachment && (
                                  <span className="flex items-center gap-1 text-green-700 dark:text-green-400 text-xs font-medium">
                                    <Paperclip className="h-3 w-3" />
                                    {part.attachmentCount}
                                  </span>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-xs gap-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReceiptPart(part);
                                  }}
                                >
                                  <FileUp className="h-3 w-3" />
                                  {hasAttachment ? 'Add' : 'Upload'}
                                </Button>
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              {part.returned ? (
                                <span className="text-xs text-muted-foreground">Returned</span>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Photo attachments */}
          {(request.attachments?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Photo of Issue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {request.attachments?.map((a, i) => {
                    const isImage = /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(a.fileName);
                    return isImage ? (
                      <button
                        key={a.id}
                        onClick={() => setLightboxIndex(i)}
                        className="h-24 w-24 overflow-hidden rounded-lg border border-border hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <img
                          src={a.viewUrl}
                          alt={a.fileName}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ) : (
                      <a
                        key={a.id}
                        href={a.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted transition-colors"
                      >
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                        {a.fileName}
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lightbox */}
          {lightboxIndex !== null && request.attachments && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
              onClick={() => setLightboxIndex(null)}
            >
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                onClick={() => setLightboxIndex(null)}
              >
                <X className="h-8 w-8" />
              </button>
              {lightboxIndex > 0 && (
                <button
                  className="absolute left-4 text-white hover:text-gray-300 z-10"
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
                >
                  <ChevronLeft className="h-10 w-10" />
                </button>
              )}
              {lightboxIndex < (request.attachments.length - 1) && (
                <button
                  className="absolute right-4 text-white hover:text-gray-300 z-10"
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
                >
                  <ChevronRight className="h-10 w-10" />
                </button>
              )}
              <img
                src={request.attachments[lightboxIndex].viewUrl}
                alt={request.attachments[lightboxIndex].fileName}
                className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <p className="absolute bottom-4 text-white text-sm text-center w-full">
                {request.attachments[lightboxIndex].fileName} ({lightboxIndex + 1} / {request.attachments.length})
              </p>
            </div>
          )}

          {/* Mechanic edit form */}
          {canEdit && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  Mechanic Form
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                    {/* Mechanic name — read-only, auto from logged-in user */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium mb-1.5">Mechanic Name</p>
                        <div className="flex h-9 w-full rounded-md border border-input bg-muted px-3 py-1 text-sm items-center">
                          {currentUser?.name ?? '—'}
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Status</FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="New">New</SelectItem>
                                <SelectItem value="In-Progress">In Progress</SelectItem>
                                <SelectItem value="Parts Ordered">Parts Ordered</SelectItem>
                                <SelectItem value="Done">Done</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="mechanicRepairDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Mechanic Repair Date</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="mechanicRepairHours"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Mechanic Repair Hours</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                max={999}
                                step="0.25"
                                placeholder="0.00"
                                {...field}
                                value={field.value ?? ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="mechanicNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mechanic Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Add repair notes, findings, actions taken…"
                              rows={4}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" disabled={updateMutation.isPending}>
                      {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
        </div>

      {/* Edit Request Dialog (for creator) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Problem / Issue</label>
              <Textarea
                rows={4}
                value={editProblem}
                onChange={(e) => setEditProblem(e.target.value)}
                placeholder="Describe the problem…"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Driver Name</label>
              <Input
                value={editDriverName}
                onChange={(e) => setEditDriverName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditOpen(false)} disabled={isSavingEdit}>
                Cancel
              </Button>
              <Button onClick={saveEdit} disabled={isSavingEdit || !editProblem.trim()}>
                {isSavingEdit ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Part Modal */}
      <Dialog open={addPartOpen} onOpenChange={setAddPartOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ordered Parts or Third Party Repairs</DialogTitle>
          </DialogHeader>
          <POForm
            repairRequest={request}
            onSuccess={() => {
              setAddPartOpen(false);
              queryClient.invalidateQueries({ queryKey: ['orderedParts', 'request', id] });
              queryClient.invalidateQueries({ queryKey: ['repairRequest', id] });
            }}
            onCancel={() => setAddPartOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Part Modal */}
      <Dialog open={!!editPart} onOpenChange={(open) => { if (!open) setEditPart(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Order — PO#{editPart?.poNumber}</DialogTitle>
          </DialogHeader>
          {editPart && (
            <POForm
              repairRequest={request}
              existingPart={editPart}
              onSuccess={() => {
                setEditPart(null);
                queryClient.invalidateQueries({ queryKey: ['orderedParts', 'request', id] });
              }}
              onCancel={() => setEditPart(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog
        open={!!receiptPart}
        onOpenChange={(open) => {
          if (!open) {
            setReceiptPart(null);
            setReceiptFile(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Receipts — PO#{receiptPart?.poNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">

            {/* Existing receipts */}
            {receiptAttachments.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Uploaded Receipts
                </p>
                <ul className="divide-y divide-border rounded-md border border-border">
                  {receiptAttachments.map((att) => (
                    <li key={att.id} className="flex items-center justify-between px-3 py-2">
                      <span className="flex items-center gap-2 text-sm truncate">
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{att.fileName}</span>
                      </span>
                      <a
                        href={att.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-3 shrink-0 text-xs font-medium text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Upload area */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Add Receipt
              </p>
              <input
                ref={receiptInputRef}
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
              />
              <div
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors',
                  receiptFile
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                )}
                onClick={() => receiptInputRef.current?.click()}
              >
                <Upload className="h-6 w-6 text-muted-foreground" />
                {receiptFile ? (
                  <p className="text-sm font-medium">{receiptFile.name}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Click to select an image or PDF
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setReceiptPart(null); setReceiptFile(null); }}
                disabled={uploading}
              >
                Close
              </Button>
              <Button onClick={handleReceiptUpload} disabled={!receiptFile || uploading}>
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
}
